import { ExternalLink, Phone, Reply, type LucideIcon } from "lucide-react";
import { Icon } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { WhatsappTemplateComponent } from "../types/whatsapp.types";
import { exampleValues, object } from "../services/template-components";
import { Section } from "./Shared";
import styles from "./TemplatePreview.module.css";

const actionIcons: Record<string, LucideIcon> = {
  QUICK_REPLY: Reply,
  URL: ExternalLink,
  PHONE_NUMBER: Phone,
};

export function TemplatePreview({
  components,
  format,
}: {
  components: WhatsappTemplateComponent[];
  format: string;
}) {
  const t = useTranslations();
  const copy = keys.whatsapp;
  return (
    <Section title={t(copy.preview)} note={t(copy.previewHint)}>
      <div
        className={styles.chat}
        style={{
          width: "100%",
          maxWidth: 448,
          marginInline: "auto",
          padding: 24,
          boxSizing: "border-box",
          borderRadius: "var(--rd-radius-card)",
        }}
      >
        <div className={styles.bubble} style={{ borderRadius: "var(--rd-radius-modal)" }}>
          {components.map((c, i) => {
            if (c.type === "BUTTONS" && Array.isArray(c.buttons))
              return (
                <div key={i}>
                  {c.buttons.map((b, index) => {
                    const button = object(b);
                    const icon = actionIcons[String(button.type)];
                    return (
                      <div
                        key={index}
                        className={styles.action}
                        style={{ fontSize: 14 }}
                        dir="auto"
                      >
                        {icon && <Icon icon={icon} size={18} color="currentColor" />}
                        <span>{String(button.text ?? button.type ?? "")}</span>
                      </div>
                    );
                  })}
                </div>
              );
            if (typeof c.text !== "string")
              return (
                <div key={i} className={`${styles.copy} ${styles.muted}`} style={{ fontSize: 13 }}>
                  {c.type}
                </div>
              );
            const samples = exampleValues(c, format);
            const text = c.text.replace(
              /\{\{([^{}]+)\}\}/g,
              (full, name) => samples[name] || full,
            );
            return (
              <p
                key={i}
                dir="auto"
                className={`${styles.copy}${c.type === "FOOTER" ? ` ${styles.muted}` : ""}`}
                style={{
                  margin: 0,
                  whiteSpace: "pre-wrap",
                  ...(c.type === "HEADER"
                    ? { fontSize: 16, fontWeight: 600 }
                    : c.type === "FOOTER"
                      ? { fontSize: 13 }
                      : { fontSize: 14 }),
                }}
              >
                {text}
              </p>
            );
          })}
        </div>
      </div>
    </Section>
  );
}
